import { SuitePayment } from "@ssc-booking/api-contracts"
import { SyntheticEvent } from "react"
import { v4 as uuidv4 } from "uuid"

export type TrackingData = {
    trackingId: string
    auid: string
}

export type PerfTrackingData = {
    apiId: string
    elapsedInMs: number
    country: string
    type: "success" | "response-error" | "network-error"
}

export const isSSR = () => typeof window === "undefined"

export const generateFormTrackingId = (): string => `${Date.now()}-${uuidv4()}`

function getVisitorAuid(): string {
    try {
        if (!isSSR() && typeof window.get_auid !== "undefined") return window.get_auid()
        return ""
    } catch {
        return ""
    }
}

export const getTrackingData = (trackingId: string): TrackingData => ({
    auid: getVisitorAuid(),
    trackingId,
})

export const trackApiPerf = (data: PerfTrackingData): void => {
    const event = { ...data, auid: getVisitorAuid() }
    // console.log(`LOADED ${event.elapsedInMs}ms`, event)
    window.wspr?.P("ecommerce_api_perf", event)
}

// NOTE: remember to also update booking-api tracking
export const trackGuestsFormSubmitted = (
    event: TrackingParams,
    bookingNumber: string,
    suitePayment: SuitePayment,
): void => {
    window.wspr?.P("web_request_submitted_raw", {
        ...trackingParamsToEvent(event),
        request_type: "WBOF.01",
        type: "guests-form-submitted",
        purchase: paymentToPurchaseTracking(bookingNumber, suitePayment),
    })
}

// NOTE: remember to also update booking-api tracking
export const trackCourtesyHold = (event: TrackingParams, bookingNumber: string, suitePayment: SuitePayment): void => {
    window.wspr?.P("web_request_submitted_raw", {
        ...trackingParamsToEvent(event),
        request_type: "WBOF.04",
        type: "guests-form-submitted",
        purchase: paymentToPurchaseTracking(bookingNumber, suitePayment),
    })
}

export const trackWebQuoteFormSubmitted = (event: TrackingParams): void => {
    window.wspr?.P("web_request_submitted_raw", {
        ...trackingParamsToEvent(event),
        request_type: "WEBQ",
        type: "web-quote-form-submitted",
    })
}

// NOTE: this tracking event is also triggered server-side within
// booking-api. If we change something here remember to also update
// the server-side code
export const trackPaymentConfirmed = (
    event: TrackingParams,
    bookingNumber: string,
    suitePayment: SuitePayment,
): void => {
    window.wspr?.P("web_request_submitted_raw", {
        ...trackingParamsToEvent(event),
        type: "payment-confirmed",
        request_type: "WBBK",
        purchase: paymentToPurchaseTracking(bookingNumber, suitePayment),
    })
}

// NOTE: this tracking event is also triggered server-side within
// booking-api. If we change something here remember to also update
// the server-side code
export const trackPaymentError = (event: TrackingParams, bookingNumber: string, suitePayment: SuitePayment): void => {
    window.wspr?.P("web_request_submitted_raw", {
        ...trackingParamsToEvent(event),
        type: "payment-error",
        request_type: "WBOF.02",
        purchase: paymentToPurchaseTracking(bookingNumber, suitePayment),
    })
}

export const trackPaymentCancelled = (
    event: TrackingParams,
    bookingNumber: string,
    suitePayment: SuitePayment,
): void => {
    window.wspr?.P("web_request_submitted_raw", {
        ...trackingParamsToEvent(event),
        type: "payment-cancelled",
        request_type: "WBOF.03",
        purchase: paymentToPurchaseTracking(bookingNumber, suitePayment),
    })
}

type TrackingParams = {
    trackingId: string
    isVsMember: boolean
    isTest?: boolean
}

interface FormTrackingEvent {
    type: string
    uwrid?: string
    is_test?: boolean //name starts with xx_
    request_type: "WBBK" | "WBOF.01" | "WBOF.02"
    isVsMember?: boolean
}

export const dispatchTrackingEvent = (
    eventName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
    detail: any,
    domEvent?: SyntheticEvent,
): void => {
    if (!isSSR()) {
        const trackingEvent = new CustomEvent(eventName, {
            bubbles: true,
            cancelable: true,
            detail: Object.freeze(detail),
        })

        const dispatchingElement = domEvent && domEvent.target ? domEvent.target : document

        dispatchingElement.dispatchEvent(trackingEvent)
    }
}

export const getAAMarket = (market: string | undefined, countryCode: string | undefined): string | undefined => {
    if (countryCode === "US" || countryCode === "CA") return "US"
    if (market === "FT") return "LAM"
    if (market === "AS") return "AP"
    if (market === "EU") return "EMEA"
    return market
}

const paymentToPurchaseTracking = (bookingNumber: string, suitePayment: SuitePayment) => {
    return {
        transaction_id: bookingNumber,
        payment_type: "Credit Card",
        currency: suitePayment.totalAmount.currency,
        revenue: suitePayment.totalAmount.value,
        //tax: "1166.50",
        //shipping: "0.00",
        //coupon: "Venetian Society", // leave empty if it's not present
    }
}

const trackingParamsToEvent = (params: TrackingParams): Partial<FormTrackingEvent> => ({
    uwrid: params.trackingId,
    is_test: params.isTest,
    isVsMember: params.isVsMember,
})
